import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { errorFromResponse } from '@/lib/errors';
import type {
  BusinessInquiryDetail,
  BusinessInquiryFilters,
  BusinessInquiryPage,
  BusinessInquirySummary,
  InquiryStatus,
  InquiryType,
  RecipientSettings,
} from '../types/businessInquiry';

/**
 * QA-628 비즈니스 문의 — partner-service(gateway #27 `/admin/v1/business-inquiries/**`).
 * generated OpenAPI 에 없는 경로라 adminFetch 로 부른다. 응답 envelope = { success, data, error, meta }.
 * 목록 page 는 요청 0부터, 응답 meta.pagination.page 는 1부터 — 화면은 1부터 들고 보낼 때 1을 뺀다.
 */
const BASE = '/admin/v1/business-inquiries';

export const businessInquiriesKey = ['admin', 'business-inquiries'] as const;
const listKey = (filters: BusinessInquiryFilters, page: number, size: number) =>
  [...businessInquiriesKey, 'list', filters, page, size] as const;
const detailKey = (no: number) => [...businessInquiriesKey, 'detail', no] as const;
export const unprocessedCountKey = [...businessInquiriesKey, 'unprocessed-count'] as const;
const recipientSettingsKey = [...businessInquiriesKey, 'recipient-settings'] as const;

const readData = async <T,>(res: Response): Promise<T> => {
  if (!res.ok) throw await errorFromResponse(res);
  const env = (await res.json()) as { data: T };
  return env.data;
};

const json = (method: string, body?: unknown): RequestInit => ({
  method,
  headers: { 'Content-Type': 'application/json' },
  ...(body === undefined ? {} : { body: JSON.stringify(body) }),
});

/** 목록·엑셀 공통 파라미터. 유형은 types=A&types=B 로 반복, 비어 있는 조건은 보내지 않는다. */
export const inquiryFilterParams = (filters: BusinessInquiryFilters): URLSearchParams => {
  const q = new URLSearchParams();
  if (filters.dateFrom) q.set('dateFrom', filters.dateFrom);
  if (filters.dateTo) q.set('dateTo', filters.dateTo);
  if (filters.mallCode) q.set('mallCode', filters.mallCode);
  for (const t of filters.types) q.append('types', t);
  if (filters.status) q.set('status', filters.status);
  const keyword = filters.keyword.trim();
  if (keyword) {
    q.set('keyword', keyword);
    if (filters.searchType) q.set('searchType', filters.searchType);
  }
  return q;
};

interface ListEnvelope {
  data: BusinessInquirySummary[] | null;
  meta?: { pagination?: { totalItems?: number; totalPages?: number } } | null;
}

export const useBusinessInquiriesQuery = (filters: BusinessInquiryFilters, page: number, size: number) =>
  useQuery<BusinessInquiryPage>({
    queryKey: listKey(filters, page, size),
    queryFn: async () => {
      const q = inquiryFilterParams(filters);
      q.set('page', String(Math.max(0, page - 1)));
      q.set('size', String(size));
      const res = await adminFetch(`${BASE}?${q.toString()}`);
      if (!res.ok) throw await errorFromResponse(res);
      const env = (await res.json()) as ListEnvelope;
      const items = env.data ?? [];
      return {
        items,
        totalItems: env.meta?.pagination?.totalItems ?? items.length,
        totalPages: env.meta?.pagination?.totalPages ?? 1,
      };
    },
    placeholderData: keepPreviousData,
  });

/** 미처리(접수 상태) 전체 건수 — 메뉴 배지·목록 상단. 검색 조건과 무관. */
export const useUnprocessedCountQuery = (enabled = true) =>
  useQuery<number>({
    queryKey: unprocessedCountKey,
    queryFn: async () => (await readData<{ count: number }>(await adminFetch(`${BASE}/unprocessed-count`))).count,
    enabled,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

export const useBusinessInquiryQuery = (inquiryNo: number | null) =>
  useQuery<BusinessInquiryDetail>({
    queryKey: detailKey(inquiryNo ?? 0),
    queryFn: async () => readData<BusinessInquiryDetail>(await adminFetch(`${BASE}/${inquiryNo}`)),
    enabled: inquiryNo !== null,
    retry: false,
  });

/** 상세를 돌려주는 변경(저장·재발송) 뒤 — 상세 캐시를 갈아 끼우고 목록·배지는 다시 불러온다. */
const useApplyDetail = () => {
  const qc = useQueryClient();
  return (detail: BusinessInquiryDetail) => {
    qc.setQueryData(detailKey(detail.inquiryNo), detail);
    void qc.invalidateQueries({ queryKey: [...businessInquiriesKey, 'list'] });
    void qc.invalidateQueries({ queryKey: unprocessedCountKey });
  };
};

export const useUpdateBusinessInquiryMutation = (inquiryNo: number) => {
  const apply = useApplyDetail();
  return useMutation<BusinessInquiryDetail, Error, { status: InquiryStatus; memo: string }>({
    mutationFn: async (body) =>
      readData<BusinessInquiryDetail>(await adminFetch(`${BASE}/${inquiryNo}`, json('PATCH', body))),
    onSuccess: apply,
  });
};

export const useResendMailMutation = (inquiryNo: number) => {
  const apply = useApplyDetail();
  return useMutation<BusinessInquiryDetail, Error, number>({
    mutationFn: async (logId) =>
      readData<BusinessInquiryDetail>(
        await adminFetch(`${BASE}/${inquiryNo}/mail-logs/${logId}/resend`, { method: 'POST' }),
      ),
    onSuccess: apply,
  });
};

/** 사업자등록증 — 비공개 저장이라 열 때마다 5분짜리 주소를 받는다. inline = 새 창 미리보기, attachment = 다운로드. */
export const fetchAttachmentUrl = async (
  inquiryNo: number,
  disposition: 'inline' | 'attachment',
): Promise<string> =>
  (
    await readData<{ url: string }>(
      await adminFetch(`${BASE}/${inquiryNo}/attachment-url?disposition=${disposition}`),
    )
  ).url;

const filenameOf = (res: Response): string => {
  const cd = res.headers.get('Content-Disposition') ?? '';
  const star = cd.match(/filename\*=UTF-8''([^;]+)/i);
  if (star?.[1]) return decodeURIComponent(star[1]);
  const plain = cd.match(/filename="?([^";]+)"?/i);
  if (plain?.[1]) return plain[1];
  return 'business-inquiries.xlsx';
};

/** 엑셀 — 목록과 같은 조건(page·size 제외), No 최신순 최대 1만 행. */
export const downloadBusinessInquiriesExcel = async (filters: BusinessInquiryFilters): Promise<void> => {
  const query = inquiryFilterParams(filters).toString();
  const res = await adminFetch(`${BASE}/export${query ? `?${query}` : ''}`);
  if (!res.ok) throw await errorFromResponse(res);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filenameOf(res);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export const useRecipientSettingsQuery = () =>
  useQuery<RecipientSettings>({
    queryKey: recipientSettingsKey,
    queryFn: async () => readData<RecipientSettings>(await adminFetch(`${BASE}/recipient-settings`)),
    // 화면이 이 값으로 편집 초안을 만든다 — 창 포커스로 다시 불러와 편집 중인 내용을 덮지 않게 한다.
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

/** 전체 교체 — 7개 유형을 모두 보낸다(빠진 유형은 담당자 없음으로 저장된다). */
export const useSaveRecipientSettingsMutation = () => {
  const qc = useQueryClient();
  return useMutation<RecipientSettings, Error, Record<InquiryType, string[]>>({
    mutationFn: async (assignments) =>
      readData<RecipientSettings>(
        await adminFetch(
          `${BASE}/recipient-settings`,
          json('PUT', {
            assignments: Object.entries(assignments).map(([type, adminUserIds]) => ({ type, adminUserIds })),
          }),
        ),
      ),
    onSuccess: (data) => qc.setQueryData(recipientSettingsKey, data),
  });
};
